// src/pages/Customer/CustomerPayment.jsx
import React, { useState, useEffect } from "react";
import Layout from "../../layout/customer/Layout";
import CustomerProjectValueSummary from "../../layout/customer/Payment/CustomerProjectValueSummary";
import CustomerProjectValueHistoryTable from "../../layout/customer/Payment/CustomerProjectValueHistoryTable";
import CustomerPaymentHistory from "../../layout/customer/Payment/CustomerPaymentHistory";
import CustomerPaymentRequest from "../../layout/customer/Payment/CustomerPaymentRequest";
import {
  ArrowLeft,
  ListChecks,
  History as HistoryIconLucide,
  CreditCard,
  AlertTriangle,
  RefreshCw,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import useCustomerPaymentStore from "../../store/customer/useCustomerPaymentStore";

const CustomerPayment = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [activeTab, setActiveTab] = useState("payments"); // Default to payments tab
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { refreshProjectData } = useCustomerPaymentStore();

  const handleGoBack = () => navigate(-1);

  const handleRefresh = async () => {
    if (!projectId) return;
    setIsRefreshing(true);
    try {
      await refreshProjectData(projectId);
    } catch (error) {
      console.error("Error refreshing data:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Auto-refresh data every 30 seconds
  useEffect(() => {
    if (!projectId) return;
    
    const interval = setInterval(() => {
      refreshProjectData(projectId);
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [projectId, refreshProjectData]);

  // Divider component
  const CenterDivider = ({ text }) => (
    <div className="flex items-center my-4 lg:my-6">
      <div className="flex-grow border-t border-gray-300"></div>
      <span className="mx-2 lg:mx-4 text-gray-400 font-semibold text-xs uppercase">{text}</span>
      <div className="flex-grow border-t border-gray-300"></div>
    </div>
  );

  // Render content based on selected tab
  const renderTabContent = () => {
    if (!projectId) {
      return (
        <div className="text-center py-10 text-red-500 bg-red-50 p-4 rounded-lg">
          <AlertTriangle className="w-12 h-12 mx-auto mb-2" />
          <p className="font-semibold">Project ID is missing.</p>
          <p className="text-sm">
            Cannot load payment details without a project context.
          </p>
        </div>
      );
    }
    if (activeTab === "payments") {
      return (
        <>
          <div className="mb-4">
            <h3 className="text-sm lg:text-base font-semibold text-gray-700 mb-2">Total Project Value</h3>
            <CustomerProjectValueHistoryTable projectId={projectId} />
          </div>
          <CenterDivider text="or" />
          <div className="mb-4">
            <h3 className="text-sm lg:text-base font-semibold text-gray-700 mb-2">Builder Payment Requests</h3>
            <CustomerPaymentRequest projectId={projectId} compact />
          </div>
        </>
      );
    }
    if (activeTab === "history") {
      return <CustomerPaymentHistory projectId={projectId} />;
    }
    return null;
  };

  const tabItems = [
    {
      id: "payments",
      label: "Payments",
      icon: <CreditCard className="w-4 h-4 mr-1.5 sm:mr-2" />,
    },
    {
      id: "history",
      label: "Payment History",
      icon: <HistoryIconLucide className="w-4 h-4 mr-1.5 sm:mr-2" />,
    },
  ];

  return (
    <Layout>
      <div className="h-full flex flex-col bg-gray-50 rounded-lg shadow-md">
        {/* Project Value Summary at the top */}
        {projectId && <CustomerProjectValueSummary projectId={projectId} />}
        
        <div className="px-3 lg:px-4 sm:px-6 py-3 border-b border-gray-200 bg-white rounded-t-lg flex-shrink-0 flex items-center sticky top-0 z-20 shadow-sm">
          <button
            onClick={handleGoBack}
            className="p-1.5 mr-2 sm:mr-3 text-gray-500 hover:text-blue-600 hover:bg-gray-100 rounded-full transition-colors duration-150"
            aria-label="Go back"
          >
            <ArrowLeft className="w-4 h-4 lg:w-5 lg:h-5" />
          </button>
          <h2 className="text-sm lg:text-base sm:text-lg font-semibold text-gray-800 flex items-center">
            <CreditCard className="w-4 h-4 lg:w-5 lg:h-5 mr-2 text-blue-500" />
            Project Financials
            {projectId && (
              <span className="text-gray-500 ml-2 text-xs lg:text-sm">
                (ID: {projectId})
              </span>
            )}
          </h2>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="ml-auto p-1.5 text-gray-500 hover:text-blue-600 hover:bg-gray-100 rounded-full transition-colors duration-150 disabled:opacity-50"
            aria-label="Refresh data"
          >
            <RefreshCw className={`w-4 h-4 lg:w-5 lg:h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>

        <div className="px-3 lg:px-4 sm:px-6 border-b border-gray-200 bg-white flex-shrink-0">
          <nav
            className="-mb-px flex space-x-2 lg:space-x-3 sm:space-x-4 overflow-x-auto pb-px"
            aria-label="Payment Tabs"
          >
            {tabItems.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`whitespace-nowrap py-2 lg:py-2.5 px-1 border-b-2 font-medium text-xs lg:text-sm flex items-center transition-colors duration-200 focus:outline-none
                  ${
                    activeTab === tab.id
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="flex-grow overflow-y-auto p-3 lg:p-4 sm:p-6">
          {renderTabContent()}
        </div>
      </div>
    </Layout>
  );
};

export default CustomerPayment;
